import { apiClient } from './client';
import type { BCUser, POSStaff } from './types';

interface PaginationInfo {
  total: number;
  count: number;
  perPage: number;
  currentPage: number;
  totalPages: number;
}

interface StaffListParams {
  isActive?: 'true' | 'false' | 'all';
  page?: number;
  limit?: number;
}

export const staffApi = {
  async list(params: StaffListParams = {}): Promise<{ staff: POSStaff[]; pagination: PaginationInfo }> {
    const searchParams = new URLSearchParams();
    if (params.isActive && params.isActive !== 'all') {
      searchParams.set('isActive', params.isActive);
    }
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));

    const query = searchParams.toString();
    return apiClient.request<{ staff: POSStaff[]; pagination: PaginationInfo }>(`/pos-staff${query ? `?${query}` : ''}`);
  },

  async create(data: {
    bcUserId?: string; // Optional - not set for local staff
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    role: 'admin' | 'user';
    locationIds?: number[];
  }): Promise<{ id: string }> {
    return apiClient.request<{ id: string }>('/pos-staff', {
      method: 'POST',
      body: data,
    });
  },

  async update(
    id: string,
    data: Partial<{
      phone: string;
      role: 'admin' | 'user';
      locationIds: number[];
      isActive: boolean;
    }>
  ): Promise<{ success: boolean }> {
    return apiClient.request<{ success: boolean }>(`/pos-staff/${id}`, {
      method: 'PUT',
      body: data,
    });
  },

  async delete(id: string): Promise<{ success: boolean }> {
    return apiClient.request<{ success: boolean }>(`/pos-staff/${id}`, {
      method: 'DELETE',
    });
  },

  async resetPIN(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.request<{ success: boolean; message: string }>(`/pos-staff/${id}/reset-pin`, {
      method: 'POST',
    });
  },

  async setPIN(id: string, pin: string): Promise<{ message: string; staffId: string }> {
    return apiClient.request<{ message: string; staffId: string }>(`/pos-staff/${id}/set-pin`, {
      method: 'POST',
      body: { pin },
    });
  },

  async updatePermissions(
    id: string,
    data: {
      preset?: 'cashier' | 'senior_cashier' | 'supervisor' | 'manager';
      custom?: {
        canProcessSales: boolean;
        canProcessRefunds: boolean;
        canVoidTransactions: boolean;
        canAccessCashDrawer: boolean;
        canAdjustInventory: boolean;
        canViewReports: boolean;
        refundLimitWithoutOTP: number;
        discountLimitWithoutOTP: number;
      };
    }
  ): Promise<{
    staff: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      permissions: {
        canProcessSales: boolean;
        canProcessRefunds: boolean;
        canVoidTransactions: boolean;
        canAccessCashDrawer: boolean;
        canAdjustInventory: boolean;
        canViewReports: boolean;
        refundLimitWithoutOTP: number;
        discountLimitWithoutOTP: number;
      };
    };
  }> {
    return apiClient.request(`/pos-staff/${id}/permissions`, {
      method: 'PUT',
      body: data,
    });
  },

  async listBCUsers(): Promise<{
    bcUsers: BCUser[];
    total: number;
    message?: string;
  }> {
    return apiClient.request('/pos-staff/bc-users');
  },

  async syncBCUsers(): Promise<{
    message: string;
    bcUsers: BCUser[];
    synced: number;
  }> {
    return apiClient.request('/pos-staff/sync-bc-users', { method: 'POST' });
  },
};
